import {Typography} from '@mui/material';
import * as React from 'react';

/**
 * React functional component representing the Privacy Content.
 * @component
 * @param {boolean} isPage - Indicates whether the content is for the Privacy Page.
 * @returns {JSX.Element} The rendered PrivacyContent component.
 */
export const PrivacyContent = isPage => {
  const titleVariant = isPage ? 'h6' : 'subtitle2';
  const bodyVariant = isPage ? 'subtitle1' : 'body2';
  return (
    <>
      <Typography variant={bodyVariant} sx={{marginTop: 2}}>
        Thank you for using our online symptom checker app, AI-MD ("App"). We are committed to protecting your privacy
        and personal information. This Privacy Policy explains how we collect, use, and disclose information about you
        when you use the App, and how we comply with PIPEDA, HIPAA, and GDPR requirements.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        1. INFORMATION WE COLLECT
      </Typography>
      <Typography variant={bodyVariant}>
        When you use the App, we may collect information about you, including:
      </Typography>
      <Typography variant={bodyVariant}>
        • Personal information: We may collect your name, email address, and other contact information if you choose to
        provide it to us.
      </Typography>
      <Typography variant={bodyVariant}>
        • Symptom information: We collect information about your symptoms and health conditions to provide you with
        accurate information and recommendations.
      </Typography>
      <Typography variant={bodyVariant}>
        • Device information: We may collect information about the device you use to access the App, including its
        operating system, browser type, and IP address.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        2. HOW WE USE INFORMATION
      </Typography>
      <Typography variant={bodyVariant}>We may use the information we collect for the following purposes:</Typography>
      <Typography variant={bodyVariant}>
        • To provide you with accurate information and recommendations based on your symptoms and health conditions.
      </Typography>
      <Typography variant={bodyVariant}>• To improve the App and personalize your experience.</Typography>
      <Typography variant={bodyVariant}>
        • To communicate with you about the App and respond to your inquiries.
      </Typography>
      <Typography variant={bodyVariant}>
        • To comply with legal and regulatory requirements, including PIPEDA, HIPAA, and GDPR.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        3. SHARING OF INFORMATION
      </Typography>
      <Typography variant={bodyVariant}>
        We may share your information with third parties in the following circumstances:
      </Typography>
      <Typography variant={bodyVariant}>
        • With your consent: We may share your information with third parties if you give us your consent to do so.
      </Typography>
      <Typography variant={bodyVariant}>
        • Service providers: We may share your information with third-party service providers who help us operate the
        App and provide services to you.
      </Typography>
      <Typography variant={bodyVariant}>
        • Legal purposes: We may share your information if we believe it is necessary to comply with applicable laws,
        regulations, or legal processes.
      </Typography>
      <Typography variant={bodyVariant}>
        • Business purposes: We may share your information if we are involved in a merger, acquisition, or other
        business transaction.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        4. DATA RETENTION
      </Typography>
      <Typography variant={bodyVariant}>
        We will retain your information for as long as necessary to fulfill the purposes for which it was collected,
        unless a longer retention period is required or permitted by law.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        5. SECURITY
      </Typography>
      <Typography variant={bodyVariant}>
        We take reasonable measures to protect your information from unauthorized access, use, or disclosure. However,
        no method of transmission over the internet or method of electronic storage is 100% secure.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        6. YOUR RIGHTS
      </Typography>
      <Typography variant={bodyVariant}>You have certain rights regarding your information, including:</Typography>
      <Typography variant={bodyVariant}>
        • Access: You have the right to access the information we hold about you.
      </Typography>
      <Typography variant={bodyVariant}>
        • Correction: You have the right to request that we correct any inaccurate or incomplete information we hold
        about you.
      </Typography>
      <Typography variant={bodyVariant}>
        • Deletion: You have the right to request that we delete your information, subject to certain limitations under
        applicable law.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        7. COMPLIANCE WITH PIPEDA, HIPAA, AND GDPR
      </Typography>
      <Typography variant={bodyVariant}>
        We are committed to complying with the requirements of PIPEDA, HIPAA, and GDPR with respect to the collection,
        use, and disclosure of your information. This includes:
      </Typography>
      <Typography variant={bodyVariant}>
        • Ensuring the confidentiality, integrity, and availability of your information.
      </Typography>
      <Typography variant={bodyVariant}>
        • Implementing appropriate technical and organizational measures to protect your information.
      </Typography>
      <Typography variant={bodyVariant}>
        • Providing you with notice of our privacy practices and your rights with respect to your information.
      </Typography>
      <Typography variant={bodyVariant}>
        • Obtaining your consent to use and disclose your information, where required.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        8. CHANGES TO THIS POLICY
      </Typography>
      <Typography variant={bodyVariant}>
        We may update this Privacy Policy from time to time. If we make material changes to the policy, we will notify
        you by email or through the App.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        9. CONSENT TO RECEIVE EMAILS
      </Typography>
      <Typography variant={bodyVariant}>
        By registering on our App, you have the option to subscribe to our email communications. If you choose to
        receive emails from AI-MD, you consent to receive informational and promotional emails from us. These emails may
        include updates about the App, health-related content, and other relevant information.
        <br />
        <br /> You have the right to opt out of these communications at any time. You can unsubscribe from our emails by
        clicking the "Unsubscribe" link provided in the email or by adjusting your communication preferences in your
        account settings.
        <br />
        <br /> Please note that even if you choose not to receive emails from us, you may still receive critical
        service-related communications, such as account-related notifications and updates to this Privacy Policy.
      </Typography>
      <Typography variant={titleVariant} sx={{marginTop: 2}}>
        10. CONTACT US
      </Typography>
      <Typography variant={bodyVariant}>
        If you have any questions or concerns about this Privacy Policy or our privacy practices, please contact us at
        info@ai-md.com.
      </Typography>
    </>
  );
};
